/**
 * Forgot Password Page
 * Página para redefinição de senha via email
 */

import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  Stack,
  Grid,
} from '@mui/material';
import { ArrowBack, Email, CheckCircle } from '@mui/icons-material';
import { supabase } from '../../lib/supabase';
import { touchFriendlyButton, touchFriendlyTextField } from '../../utils/mobileFriendlyStyles';
import AppFooter from '../AppFooter';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validação básica
    if (!email.trim()) {
      setError('Por favor, insira seu email');
      setLoading(false);
      return;
    }

    // Validação de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor, insira um email válido');
      setLoading(false);
      return;
    }

    try {
      // Enviar email de recuperação de senha
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) {
        setError(resetError.message || 'Erro ao enviar email de recuperação');
        setLoading(false);
        return;
      }

      // Sucesso
      setSuccess(true);
    } catch (err) {
      console.error('Erro ao recuperar senha:', err);
      setError('Erro ao enviar email. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: '#121212',
        }}
      >
        <Box component="main" sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <Container maxWidth="lg">
            <Grid container spacing={0} sx={{ minHeight: { md: '80vh' } }}>
              {/* Branding à esquerda */}
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  p: { xs: 4, md: 6 },
                }}
              >
                <Box textAlign="center">
                  <Box
                    component="img"
                    src="/setima-logo.svg"
                    alt="Sétima"
                    sx={{ width: { xs: 180, md: 240 }, height: 'auto', mb: 3 }}
                  />
                  <Typography variant="h4" sx={{ fontWeight: 300, color: 'rgba(255,255,255,0.92)' }}>
                    Email enviado com sucesso
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2, color: 'rgba(255,255,255,0.8)' }}>
                    Verifique sua caixa de entrada para redefinir a senha.
                  </Typography>
                </Box>
              </Grid>

              {/* Card à direita */}
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: { xs: 3, md: 4 },
                }}
              >
                <Paper
                  elevation={8}
                  sx={{
                    p: { xs: 3, md: 5 },
                    borderRadius: 3,
                    backgroundColor: 'rgba(255,255,255,0.98)',
                    maxWidth: 520,
                    width: '100%',
                  }}
                >
                  <Stack spacing={3} alignItems="center">
                    <CheckCircle sx={{ fontSize: 64, color: 'success.main' }} />
                    <Typography variant="h5" align="center">
                      Email Enviado!
                    </Typography>
                    <Alert severity="success" sx={{ width: '100%' }}>
                      <Typography variant="body2" paragraph>
                        Enviamos um link de recuperação de senha para:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>
                        {email}
                      </Typography>
                      <Typography variant="body2" paragraph>
                        Verifique sua caixa de entrada (e spam) e clique no link para redefinir sua senha.
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        O link expira em 1 hora.
                      </Typography>
                    </Alert>
                    <Button
                      component={RouterLink}
                      to="/login"
                      variant="contained"
                      fullWidth
                      sx={{
                        ...touchFriendlyButton,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #16213e 0%, #0f1419 100%)',
                        },
                      }}
                    >
                      Voltar para Login
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>
        <AppFooter mode="dark" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#121212',
      }}
    >
      <Box component="main" sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="lg">
          <Grid container spacing={0} sx={{ minHeight: { md: '80vh' } }}>
            {/* Branding à esquerda */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: { xs: 4, md: 6 },
                color: 'white',
              }}
            >
              <Box sx={{ mb: 6 }}>
                <Box
                  component="img"
                  src="/setima-logo.svg"
                  alt="Sétima"
                  sx={{ width: { xs: 180, md: 240 }, height: 'auto' }}
                />
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 300,
                  color: 'rgba(255,255,255,0.92)',
                  textAlign: 'center',
                  mb: 2,
                }}
              >
                Recuperar Senha
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  textAlign: 'center',
                  maxWidth: 560,
                  color: 'rgba(255,255,255,0.82)',
                }}
              >
                Informe seu email cadastrado e enviaremos um link para redefinir sua senha.
              </Typography>
            </Grid>

            {/* Formulário à direita */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 3, md: 4 },
              }}
            >
              <Paper
                elevation={8}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 3,
                  backgroundColor: 'rgba(255,255,255,0.98)',
                  maxWidth: 520,
                  width: '100%',
                }}
              >
                {/* Voltar */}
                <Button
                  component={RouterLink}
                  to="/login"
                  startIcon={<ArrowBack />}
                  sx={{ alignSelf: 'flex-start', mb: 2 }}
                >
                  Voltar para Login
                </Button>

                {/* Título */}
                <Typography
                  variant="h5"
                  component="h1"
                  gutterBottom
                  sx={{ fontWeight: 600, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                >
                  <Email />
                  Esqueceu a senha?
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                  Digite seu email para receber o link de recuperação
                </Typography>

                {error && (
                  <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <Stack spacing={2.5}>
                    <TextField
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                      required
                      disabled={loading}
                      sx={touchFriendlyTextField}
                      helperText="Use o email cadastrado na plataforma"
                      autoFocus
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={loading}
                      sx={{
                        ...touchFriendlyButton,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #16213e 0%, #0f1419 100%)',
                        },
                      }}
                    >
                      {loading ? 'Enviando...' : 'Enviar link de recuperação'}
                    </Button>
                  </Stack>
                </form>

                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Lembrou da senha?{' '}
                    <Link component={RouterLink} to="/login" underline="hover">
                      Fazer login
                    </Link>
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Não tem acesso?{' '}
                    <Link component={RouterLink} to="/solicitar-acesso" underline="hover">
                      Solicite aqui
                    </Link>
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <AppFooter mode="dark" />
    </Box>
  );
}

export default ForgotPasswordPage;
